import { message } from 'antd'
import { RevisionOption } from 'features/reporting/types'
import { RugFormDataState } from 'pages/reporting/reports/rug/rugReportData'
import { useCallback, useState } from 'react'
import { GeckoReportingMetadata } from 'types'

import { getSelectedFormData } from './useFormData'

// RUG report specific section selection
export interface RugSectionSelection {
  cover: boolean
  summary: boolean
  dataSheet: boolean
  images: boolean
  assetDrawings: boolean
}

interface UseReportRevisionsProps {
  metadataEntries: readonly GeckoReportingMetadata[]
  currentRevisionNumber: number
  revisions: RevisionOption[]
  onUpdateFormData: (data: Partial<RugFormDataState>) => void
}

interface UseReportRevisionsResult {
  selectedRevisionData?: RugFormDataState
  loading: boolean
  loadRevisionData: (revisionId: string) => Promise<void>
  copyRevisionContent: (sections: RugSectionSelection) => Promise<void>
  clearSelectedRevision: () => void
  hasPublishedRevisions: boolean
  canRevertToCantilever: boolean
}

export const useReportRevisions = ({
  metadataEntries,
  currentRevisionNumber,
  revisions,
  onUpdateFormData,
}: UseReportRevisionsProps): UseReportRevisionsResult => {
  const [selectedRevisionData, setSelectedRevisionData] =
    useState<RugFormDataState>()
  const [selectedRevisionId, setSelectedRevisionId] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [copyingContent, setCopyingContent] = useState(false)

  const loadRevisionData = useCallback(
    async (revisionId: string) => {
      setLoading(true)
      try {
        const revisionData = getSelectedFormData(revisionId, metadataEntries)

        if (revisionData) {
          setSelectedRevisionData(revisionData)
          setSelectedRevisionId(revisionId)
          message.success('Revision data loaded successfully')
        } else {
          throw new Error('No revision data found')
        }
      } catch (error) {
        console.error('Failed to load revision data:', error)
        message.error('Failed to load revision data')
        setSelectedRevisionData(undefined)
        setSelectedRevisionId('')
      } finally {
        setLoading(false)
      }
    },
    [metadataEntries]
  )

  // Copy revision content function
  const copyRevisionContent = useCallback(
    async (sections: RugSectionSelection) => {
      if (!selectedRevisionData || !selectedRevisionId) {
        message.error('No revision data selected')
        return
      }

      setCopyingContent(true)
      try {
        const contentToCopy: Partial<RugFormDataState> = {}

        // Copy basic content based on sections
        if (sections.cover) {
          contentToCopy.coverSectionData = selectedRevisionData.coverSectionData
          // Copy relevant main info when copying cover section
          if (selectedRevisionData.mainInfo) {
            contentToCopy.mainInfo = {
              ...contentToCopy.mainInfo,
              ...selectedRevisionData.mainInfo,
            }
          }
        }

        if (sections.summary) {
          contentToCopy.summarySectionData =
            selectedRevisionData.summarySectionData
        }

        if (sections.dataSheet) {
          contentToCopy.dataSheetSectionData =
            selectedRevisionData.dataSheetSectionData
          // Also copy relevant main info when copying data sheet
          if (selectedRevisionData.mainInfo) {
            contentToCopy.mainInfo = {
              ...contentToCopy.mainInfo,
              ...selectedRevisionData.mainInfo,
            }
          }
        }

        if (sections.images) {
          contentToCopy.imagesSectionData =
            selectedRevisionData.imagesSectionData
        }

        if (sections.assetDrawings) {
          contentToCopy.assetDrawings = selectedRevisionData.assetDrawings
        }

        onUpdateFormData(contentToCopy)
        message.success('Successfully copied content from past revision')
      } catch (error) {
        console.error('Failed to copy revision content:', error)
        message.error('Failed to copy some content from past revision')
      } finally {
        setCopyingContent(false)
      }
    },
    [selectedRevisionData, selectedRevisionId, onUpdateFormData]
  )

  const clearSelectedRevision = useCallback(() => {
    setSelectedRevisionData(undefined)
    setSelectedRevisionId('')
  }, [])

  const hasPublishedRevisions = revisions.some(
    (rev) => rev.isPublished && rev.revisionNumber < currentRevisionNumber
  )

  const canRevertToCantilever = Boolean(
    revisions.find((rev) => rev.revisionNumber === currentRevisionNumber)
      ?.isPublished === false
  )

  return {
    selectedRevisionData,
    loading: loading || copyingContent,
    loadRevisionData,
    copyRevisionContent,
    clearSelectedRevision,
    hasPublishedRevisions,
    canRevertToCantilever,
  }
}
